import React, { useState } from "react";
import {
  Card,
  Space,
  Button,
  Table,
  Typography,
  Row,
  Col,
  Statistic,
  Input,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { motion } from "framer-motion";
import {
  useReportsServiceDownloadStockValuationReportV2,
  useReportsServiceGetStockValuationReportV2,
} from "api/queries";

const { Title } = Typography;

const StockValuationReport = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState("");

  const offset = (page - 1) * limit;

  const { data, isLoading, isFetching } = useReportsServiceGetStockValuationReportV2({
    limit,
    offset,
    search,
  });

  const { mutate: downloadReport, isPending: isDownloadPending } =
    useReportsServiceDownloadStockValuationReportV2();

  const columns = [
    {
      title: "Product",
      dataIndex: "productName",
      key: "productName",
    },
    {
      title: "SKU",
      dataIndex: "sku",
      key: "sku",
    },
    {
      title: "Brand",
      dataIndex: "brandName",
      key: "brandName",
    },
    {
      title: "Category",
      dataIndex: "categoryName",
      key: "categoryName",
    },
    {
      title: "Subcategory",
      dataIndex: "subCategoryName",
      key: "subCategoryName",
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
    },
    {
      title: "Total Buying Price",
      dataIndex: "totalBuyingPrice",
      key: "totalBuyingPrice",
      render: (value: number) => value,
    },
    {
      title: "Total Valuation",
      dataIndex: "totalValuation",
      key: "totalValuation",
      render: (value: number) => value,
    },
    {
      title: "Total Selling Price",
      dataIndex: "totalSellingPrice",
      key: "totalSellingPrice",
      render: (value: number) => value,
    },
  ];

  const handleTableChange = (pagination: any) => {
    if (pagination && pagination.current && pagination.pageSize) {
      setPage(pagination.current || 1);
      setLimit(pagination.pageSize || 20);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleDownload = () => {
    downloadReport(
      {},
      {
        onSuccess: (response: any) => {
          if (response?.downloadUrl) {
            window.open(response.downloadUrl, '_blank');
          }
        },
      }
    );
  };

  // KPIs from API
  const kpis = data?.kpis;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5, delay: 0 }}
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Title level={2}>Stock Valuation Report</Title>
          <Space>
            <Input.Search
              value={search}
              onChange={handleSearchChange}
              placeholder="Search"
              style={{ width: 200 }}
              allowClear
            />
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={isDownloadPending}
              onClick={handleDownload}
            >
              Download Report
            </Button>
          </Space>
        </Space>

        {/* KPIs Row */}
        <Row gutter={16}>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Products"
                value={kpis?.totalProducts ?? 0}
                loading={isLoading || isFetching}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Quantity"
                value={kpis?.totalQuantity ?? 0}
                loading={isLoading || isFetching}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Valuation"
                value={kpis?.totalValuation ?? 0}
                loading={isLoading || isFetching}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Buying Price"
                value={kpis?.totalBuyingPrice ?? 0}
                loading={isLoading || isFetching}
              />
            </Card>
          </Col>
        </Row>

        {/* Main Data Table */}
        <Table
          columns={columns}
          dataSource={data?.items || []}
          loading={isLoading || isFetching}
          rowKey={(item) => item.productId}
          scroll={{ x: "max-content" }}
          pagination={{
            current: data?.pagination?.offset !== undefined ? Math.floor(data.pagination.offset / data.pagination.limit) + 1 : page,
            pageSize: data?.pagination?.limit || limit,
            total: data?.pagination?.total || 0,
            onChange: (page, pageSize) => {
              setPage(page);
              setLimit(pageSize);
            },
            pageSizeOptions: [10, 20, 50, 100],
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          }}
          onChange={handleTableChange}
        />
      </Space>
    </motion.div>
  );
};

export default StockValuationReport;
